import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createWarehouseEquipment,
  getWarehouseEquipment,
  toggleWarehouseEquipmentStatus,
  updateWarehouseEquipment,
} from "../../api/warehouse/equipment";
import { EquipmentFormType } from "../../types/forms/warehouse/equipment";

const queryKey = ["warehouse", "equipment"];

export const useWarehouseEquipment = () => {
  const queryClient = useQueryClient();

  const result = useQuery({
    queryKey,
    queryFn: () => getWarehouseEquipment(),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const createMutation = useMutation({
    mutationFn: (values: EquipmentFormType) => createWarehouseEquipment(values),
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: ({ equipmentId, values }: { equipmentId: number; values: EquipmentFormType }) =>
      updateWarehouseEquipment(equipmentId, values),
    onSuccess: invalidate,
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (equipmentId: number) => toggleWarehouseEquipmentStatus(equipmentId),
    onSuccess: invalidate,
  });

  return {
    equipmentData: result.data ?? [],
    isPending: result.isPending,
    isFetching: result.isFetching,
    isError: result.isError,
    error: result.error,
    refetch: result.refetch,
    createEquipment: createMutation.mutateAsync,
    updateEquipment: updateMutation.mutateAsync,
    toggleEquipmentStatus: toggleStatusMutation.mutateAsync,
    isUpdating:
      createMutation.isPending || updateMutation.isPending || toggleStatusMutation.isPending,
  };
};